import { ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { Store } from '@ngxs/store';
import { ExplanationJsonModel, Pages } from '../../explanation.json.model';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { DatePipe, NgClass } from '@angular/common';
import { MatIconButton, MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { getStatusText, getStatusColor, StateFilterCBComponent } from '../../../utils/component/state-filter-combobox/state-cb.component';
import { ConsumerFilterCBComponent } from '../../../utils/component/consumer-filter-combobox/consumer-cb.component';
import { CompetencyFilterCBComponent } from '../../../utils/component/competency-filter-combobox/competency-cb.component';
import { FreeFilterInputComponent } from '../../../utils/component/free-filter-input/free-filter-input.component';
import { MainPaginationComponent } from '../../../core/components/main-pagination/main-pagination.component';
import { ExplanationService } from '../../services/explanation.service';
import { FetchOptionsState } from '../../../core/state/fetchOptions.state';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { EditExplanationComponent } from '../../edit-explanation/edit-explanation/edit-explanation.component';
import { FetchOptionsActions } from '../../../core/state/fetchOptions.actions';
import { ConfirmDialogComponent } from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { CollectionFilter } from '../../../core/jsonApi/BaseCollectionService';
import { Approval } from '../../../core/types';
import { ConsumerJsonModel } from '../../../consumer/consumer.json.model';
import { SubjectJsonModel } from '../../../subject/subject.json.model';
import { AppSelectors } from '../../../app.state';
import { CopyDialogComponent } from '../../../core/components/copy-dialog/copy-dialog.component';
import { cloneModel } from '@datx/core';
import { getMinFilterLengthText } from '../../../core/util';
import { PlayerMessageService } from '../../../core/services/player.service';
import { PreviewApiService } from '../../../core/services/preview-api.service';
import { WidgetMessageService } from '../../../core/services/widget-message.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LocalStorageService } from '../../../core/services/local-storage.service';

@Component({
  selector: 'pixl-main-explanation',
  imports: [
    MatIcon,
    MatSortModule,
    MatIconButton,
    MatTableModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    MatDialogModule,
    MatListModule,
    MatInputModule,
    DatePipe,
    NgClass,
    StateFilterCBComponent,
    ConsumerFilterCBComponent,
    CompetencyFilterCBComponent,
    FreeFilterInputComponent,
    MainPaginationComponent,
    TranslocoModule,
  ],
  providers: [],
  templateUrl: './explanation.component.html',
  styleUrl: './explanation.component.scss'
})
export class ExplanationComponent implements OnInit {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('editStateDiv') editStateRef!: ElementRef;

  private readonly store = inject(Store);
  private dialog = inject(MatDialog);
  private stateFilter: string | undefined;
  private consumerFilter: string | undefined;
  private competencyFilter: string | undefined;
  private freeFilter: string | undefined;
  private explanationService = inject(ExplanationService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private _liveAnnouncer = inject(LiveAnnouncer);
  private LIST_FIELDS: string[] | null = ['name', 'approval', 'pages', 'consumerId', 'subjectId', 'consumerData', 'config'];
  private sortFieldName: string | null = null;
  private sortFieldDirection: string | null = null;
  private appState$ = this.store.select(AppSelectors.appState);
  private playerService = inject(PlayerMessageService);
  private previewApiService = inject(PreviewApiService);
  private widgetService = inject(WidgetMessageService);
  private translocoService = inject(TranslocoService);
  private localStorageService = inject(LocalStorageService);

  public consumers: ConsumerJsonModel[] = []; // List of all available consumers
  public competencies: SubjectJsonModel[] = []; // List of all available competencies
  public dataSource = new MatTableDataSource<ExplanationJsonModel>([] as ExplanationJsonModel[]);
  public searchFilterHint = signal(false);
  public readonly freeFilterLen = 3; // Min length of free filter input
  public displayedColumns: string[] = ['edit-actions', 'id', 'name', 'consumer', 'competency', 'pages', 'updated', 'status'];
  public pageSize = signal(25);
  public filters: CollectionFilter[] = [];
  public currentSelectedRow = signal<string | undefined>(undefined);
  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;

  private setFilters() {
    this.currentSelectedRow.set(undefined); // unmark the select row

    this.filters = [];

    if (this.consumerFilter) {
      this.filters.push({ fieldName: 'consumerId', value: this.consumerFilter });
    }

    if (this.competencyFilter) {
      this.filters.push({ fieldName: 'subjectId', value: this.competencyFilter });
    }

    if (this.stateFilter) {
      this.filters.push({ fieldName: 'approval', value: this.stateFilter });
    }

    if (this.freeFilter) {
      this.filters.push({ fieldName: 'free', value: this.freeFilter });
    }

    this.getListData();
  }

  private sortData(sort: Sort): void {
    this.store.dispatch(new FetchOptionsActions.UpdateSortFieldName(sort.active));
    this.store.dispatch(new FetchOptionsActions.UpdateSortDirection(sort.direction));
    this.getListData();
  }

  private setDefaultSort() {
    this.sortFieldName = 'name';
    this.sortFieldDirection = 'asc';
  }

  public ngOnInit(): void {
    this.dataSource.sort = this.sort;
    this.setDefaultSort();
    this.appState$.subscribe(({ isLoaded, consumers, subjects }) => {
      if (isLoaded) {
        this.consumers = consumers;
        this.competencies = subjects;
        this.getListData();
      }
    });
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  public getCreatedDate(id: string): Date {
    return new Date(parseInt(id.substring(0, 8), 16) * 1000);
  }

  public getStateText(approval: Approval): string {
    return this.translocoService.translate(getStatusText(approval));
  }

  public getStateColor(approval: Approval): string {
    return getStatusColor(approval);
  }

  public setConsumerFilter(ev: string): void {
    this.consumerFilter = ev;
    this.setFilters();
  }

  public setCompetencyFilter(ev: string): void {
    this.competencyFilter = ev;
    this.setFilters();
  }

  public setStateFilter(ev: string): void {
    this.stateFilter = ev;
    this.setFilters();
  }

  public setFreeFilter(ev: string): void {
    this.freeFilter = (ev.length === 0 ? undefined : ev);
    this.setFilters();
  }

  public getPages(val: Pages[]): number {
    if (val) {
      return val.length;
    }
    return 0;
  }

  public getSearchFilterHint(): string {
    return getMinFilterLengthText(this.freeFilterLen);
  }

  public changeExplanationState(exercise: ExplanationJsonModel) {
    if (exercise.approval === 'Dormant') {
      return;
    }

    if (this.editStateRef.nativeElement.classList.contains('open')) {
      this.editStateRef.nativeElement.classList.remove('open');
    }
    else {
      this.editStateRef.nativeElement.classList.add('open');
    }
  }

  private updateView(explanation: ExplanationJsonModel): void {
    if (this.consumers) {
      const consumer = this.consumers.find((entry) => {
        return entry.id === explanation.consumerId;
      });
      if (consumer) {
        explanation.setConsumer(consumer);
      }
    }

    if (this.competencies && typeof (explanation.getCompetency()) !== 'undefined') {
      const competency = this.competencies.find((entry) => {
        return entry.id === explanation.getCompetency().id;
      });
      if (competency) {
        explanation.setCompetency(competency);
      }
    }

    this.cdr.markForCheck();
  }

  private updateCompetencyView(explanation: ExplanationJsonModel): void {
    if (this.competencies) {
      const subject = this.competencies.find((entry) => {
        return entry.treeId === explanation.subjectId;
      });

      if (subject) {
        explanation.setCompetency(subject);
      }
    }
  }

  private updateConsumerView(explanation: ExplanationJsonModel): void {
    if (this.consumers) {
      const consumer = this.consumers.find((entry) => {
        return entry.id === explanation.consumerId;
      });

      if (consumer) {
        explanation.setConsumer(consumer);
      }
    }
  }

  private showExplanationDialog(editExplanation: ExplanationJsonModel): void {
    const dialogRef = this.dialog.open(EditExplanationComponent, {
      minWidth: '40vw',
      maxWidth: '90vw',
      height: '80vh',
      data: editExplanation
    });

    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((explanation: ExplanationJsonModel) => {
        if (explanation) {
          if (!explanation.hasId()) {
            // POST
            this.addExplanation2DB(explanation, false);
          }
          else {
            // PATCH
            this.explanationService.update(explanation).subscribe((explanationFromServer: ExplanationJsonModel) => {
              this.dataSource.data = this.dataSource.data.map(item => item.id === explanationFromServer.id ? explanationFromServer : item);
            });

            this.updateCompetencyView(explanation);
            this.updateConsumerView(explanation);

            this.cdr.markForCheck();
          }
        }
      });
  }

  private addExplanation2DB(explanation: ExplanationJsonModel, showEditDialog: boolean) {
    this.explanationService
      .createAndSave(explanation)
      .subscribe((explanationFromServer: ExplanationJsonModel) => {
        this.dataSource.data.push(explanationFromServer);
        this.store.dispatch(new FetchOptionsActions.UpdateTotalItems(this.store.selectSnapshot(FetchOptionsState.getTotal) + 1)); // Update paginator

        this.updateConsumerView(explanationFromServer);
        this.updateCompetencyView(explanationFromServer);
        this.getListData();

        if (showEditDialog) {
          this.showExplanationDialog(explanationFromServer);
        }
      });
  }

  public addExplanation(): void {
    this.showExplanationDialog(new ExplanationJsonModel({
      active: true
    }));
  }

  public editExplanation(explanation: ExplanationJsonModel): void {
    this.currentSelectedRow.set(explanation.id);
    this.showExplanationDialog(explanation);
  }

  public getListData(): void {
    this.explanationService.getAllWithPagination({}, {
      pageNumber: this.store.selectSnapshot(FetchOptionsState.getPageNumber),
      pageSize: this.store.selectSnapshot(FetchOptionsState.getPageSize),
      fields: this.LIST_FIELDS,
      filters: this.filters,
      sortFieldName: this.store.selectSnapshot(FetchOptionsState.getSortFieldName),
      sortDirection: this.store.selectSnapshot(FetchOptionsState.getSortDirection)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const [data] = response;
          const explanations = data.map(r => {
            r.setConsumer(this.consumers.find((model) => model.id == r.consumerId) as ConsumerJsonModel);
            r.setCompetency(this.competencies.find((model) => model.treeId == r.subjectId) as SubjectJsonModel);
            return r;
          });

          this.dataSource = new MatTableDataSource<ExplanationJsonModel>(explanations);
          this.cdr.markForCheck();
        },
        error: () => {
          // is handled in requestInterceptor.ts
        },
      });
  }

  public paginationChanged = (): void => {
    this.getListData();
  };

  public editContentExplanation(explanation: ExplanationJsonModel) {
    this.currentSelectedRow.set(explanation.id);
    alert(`Edit content exercise ${explanation.name}`);
  }

  public copyExplanation(explanation: ExplanationJsonModel) {
    this.currentSelectedRow.set(explanation.id);

    const dialogRef = this.dialog.open(CopyDialogComponent, {
      minWidth: '400px',
      data: {
        name: explanation.name + ' Kopie',
        consumerId: explanation.consumerId,
        competencyId: explanation.getCompetency().id,
        treeId: explanation.getCompetency().treeId
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((answer) => {
        if (answer) {
          // Get the full record from the db
          this.explanationService
            .getOneWithId(explanation.id, {})
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((explanationFromServer) => {
              // Clone the record
              const copyExplanation = new ExplanationJsonModel();

              copyExplanation.subjectId = answer.treeId;
              copyExplanation.consumerId = answer.consumerId;
              copyExplanation.name = answer.name ? answer.name : (explanationFromServer.name + ' Kopie');
              copyExplanation.sortName = explanationFromServer.sortName;
              copyExplanation.topics = explanationFromServer.topics;
              copyExplanation.assets = explanationFromServer.assets;
              copyExplanation.pages = cloneModel(explanationFromServer.pages);
              copyExplanation.config = explanationFromServer.config;
              copyExplanation.copyRestricted = explanationFromServer.copyRestricted;
              copyExplanation.approval = explanationFromServer.approval;
              copyExplanation.active = explanationFromServer.active;

              // Save the record
              this.addExplanation2DB(copyExplanation, true);
            });
        }
      });
  }

  public confirmDelete(explanation: ExplanationJsonModel) {
    this.currentSelectedRow.set(explanation.id);

    const dialogRef = this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          id: explanation.id,
          name: explanation.name,
          itemType: 'Erklärung',
          deleteFn: () => {
            return explanation.destroy();
          }
        },
      });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: boolean) => {
        if (result) {
          this.dataSource.data = this.dataSource.data.filter(value => value.id !== explanation.id);
          this.store.dispatch(new FetchOptionsActions.UpdateTotalItems(this.store.selectSnapshot(FetchOptionsState.getTotal) - 1)); // Update paginator
          this.cdr.markForCheck();
          this.getListData();
        }
      });
  }

  public previewPlayer2(exercise: ExplanationJsonModel) {
    this.previewApiService.upsertPreview('explanation', exercise.id, exercise).subscribe({
      next: () => this.playerService.openPlayerPreview(exercise.consumerId, 'explanation', exercise.id),
      error: () => this.widgetService.setMessage({
        message: this.translocoService.translate('preview.saveError'),
        messageTye: 'mtError'
      })
    });
  }
}
